import React from 'react'
import { faFileArrowUp, faRotateLeft } from '@fortawesome/free-solid-svg-icons'
import {
  colors,
  DcButton,
  DcDiv,
  DcDropDown,
  DcFlexBox,
  DcText,
  PixelFactoryContext,
  PixelIcon,
} from 'pixel-base'
import type { AjaxError, AjaxResponse } from 'rxjs/ajax'
import styled from 'styled-components'
import {
  submitBulkSalesUpload,
  type SubmitBulkSalesPayload,
  type SalesHistoryProductInput,
} from 'utils/api-routes-planning'
import type { BulkUploadResponse } from 'utils/api-routes-planning'
import * as XLSX from 'xlsx'

import { IPixelFactory } from '../../../../../decl.d'

export interface BulkSalesUploadResult {
  sale_channel: string
  total_products: number
  total_qty_sold: number
  sale_month: number
  growth_factor: number
  created_at: { date: string; timezone_type: number; timezone: string }
  updated_at: string
}

interface ParsedSalesRow {
  productSku: string
  productName: string
  quantity: number
  month?: string
  createdAt?: string
}

type SheetRow = Record<
  string,
  string | number | boolean | Date | null | undefined
>

interface BulkSalesUploadProps {
  parsedSales?: ParsedSalesRow[]
  onSalesParsed?: (sales: ParsedSalesRow[]) => void
  onClose?: () => void
}

const DropZone = styled.label<{ $isDragging: boolean }>`
  width: 100%;
  border: 1px dashed
    ${({ $isDragging }) => ($isDragging ? '#5c4dff' : '#dcdfe6')};
  border-radius: 12px;
  padding: 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 12px;
  cursor: pointer;
  background-color: ${({ $isDragging }) =>
    $isDragging ? 'rgba(92,77,255,0.05)' : 'transparent'};
  transition: all 0.2s ease-in-out;
`

const PreviewList = styled.div`
  width: 100%;
  max-height: 240px;
  overflow-y: auto;
  border: 1px solid #f0f1f5;
  border-radius: 10px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const PreviewRow = styled.div`
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid #f6f7fb;
  padding-bottom: 10px;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`

const ErrorText = styled.span`
  color: #d93025;
  font-size: 13px;
`

const ACCEPTED_MIME_TYPES = [
  'text/csv',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]

const MONTH_OPTIONS = [
  { value: '', label: 'All months' },
  { value: 'January', label: 'January' },
  { value: 'February', label: 'February' },
  { value: 'March', label: 'March' },
  { value: 'April', label: 'April' },
  { value: 'May', label: 'May' },
  { value: 'June', label: 'June' },
  { value: 'July', label: 'July' },
  { value: 'August', label: 'August' },
  { value: 'September', label: 'September' },
  { value: 'October', label: 'October' },
  { value: 'November', label: 'November' },
  { value: 'December', label: 'December' },
]

const YEAR_OPTIONS = (() => {
  const currentYear = new Date().getFullYear()
  const years: { value: string; label: string }[] = []
  for (let offset = -2; offset <= 2; offset++) {
    const year = (currentYear + offset).toString()
    years.push({ value: year, label: year })
  }
  return [{ value: '', label: 'All years' }, ...years]
})()

const CHANNEL_OPTIONS = [
  { value: '', label: 'All channels' },
  { value: 'e-commerce', label: 'E-Commerce' },
  { value: 'distribution', label: 'Distribution' },
  { value: 'retail', label: 'Retail' },
]

const normalizeKey = (value: string) =>
  value.replace(/[\s_-]/g, '').toLowerCase()

const BulkSalesUpload: React.FC<BulkSalesUploadProps> = ({
  parsedSales = [],
  onSalesParsed,
  onClose,
}) => {
  const { showFailure, showSuccess, showPageLoader, hidePageLoader } =
    React.useContext<IPixelFactory>(PixelFactoryContext)
  const [localSales, setLocalSales] = React.useState<ParsedSalesRow[]>(
    parsedSales || [],
  )
  const [fileName, setFileName] = React.useState('')
  const [error, setError] = React.useState('')
  const [isDragging, setIsDragging] = React.useState(false)
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [selectedMonth, setSelectedMonth] = React.useState('')
  const [selectedYear, setSelectedYear] = React.useState('')
  const [selectedChannel, setSelectedChannel] = React.useState('e-commerce')
  const fileInputRef = React.useRef<HTMLInputElement | null>(null)

  const handleDownloadTemplate = () => {
    const templateRows = [
      {
        product_sku: 'SKU-001',
        product_name: 'Sample Product',
        qty_sold: 10,
      },
      {
        product_sku: '',
        product_name: '',
        qty_sold: '',
      },
    ]

    const worksheet = XLSX.utils.json_to_sheet(templateRows)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sales')
    XLSX.writeFile(workbook, 'sales_upload_template.xlsx')
  }

  const extractValue = (row: SheetRow, matcherKeys: string[]): string => {
    const matchedKey = Object.keys(row).find((key) =>
      matcherKeys.includes(normalizeKey(key)),
    )

    if (!matchedKey) {
      return ''
    }

    const value = row[matchedKey]
    if (value === undefined || value === null) {
      return ''
    }

    return value.toString().trim()
  }

  const mapRowsToSales = (rows: SheetRow[]): ParsedSalesRow[] => {
    return rows
      .map((row) => {
        const productSku = extractValue(row, [
          'product_sku',
          'productsku',
          'sku',
        ])
        const productName = extractValue(row, [
          'product_name',
          'productname',
          'name',
        ])
        const qtyRaw = extractValue(row, [
          'qty_sold',
          'qtysold',
          'qty',
          'quantity',
        ])

        const quantity = qtyRaw ? Number(qtyRaw) || 0 : 0

        return {
          productSku,
          productName,
          quantity,
        }
      })
      .filter(
        (item) =>
          item.productSku &&
          item.productName &&
          item.quantity !== null &&
          item.quantity >= 0,
      )
  }

  const processFile = (file: File) => {
    const isAccepted =
      ACCEPTED_MIME_TYPES.includes(file.type) ||
      file.name.endsWith('.csv') ||
      file.name.endsWith('.xls') ||
      file.name.endsWith('.xlsx')

    if (!isAccepted) {
      const message = 'Please upload a CSV or Excel file (.csv, .xls, .xlsx).'
      setError(message)
      showFailure(message)
      return
    }

    const reader = new FileReader()
    reader.onload = (event: ProgressEvent<FileReader>) => {
      try {
        const buffer = event.target?.result
        if (!buffer) {
          throw new Error('Unable to read file')
        }

        const data = new Uint8Array(buffer as ArrayBuffer)
        const workbook = XLSX.read(data, { type: 'array' })
        const firstSheetName = workbook.SheetNames[0]
        const worksheet = workbook.Sheets[firstSheetName]
        const jsonData = XLSX.utils.sheet_to_json<SheetRow>(worksheet, {
          defval: '',
        })

        if (jsonData.length === 0) {
          throw new Error('Uploaded sheet is empty.')
        }

        const sales = mapRowsToSales(jsonData)
        if (sales.length === 0) {
          throw new Error('No valid rows found. Please follow the template.')
        }

        // Update state with parsed sales
        setLocalSales(sales)
        setFileName(file.name)
        setError('')
        onSalesParsed?.(sales)
        showSuccess(`Parsed ${sales.length} sales rows successfully`)
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Unable to process the file'
        setError(message)
        setLocalSales([])
        onSalesParsed?.([])
        showFailure(message)
      }
    }

    reader.readAsArrayBuffer(file)
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFile = event.target.files?.[0]
    if (!uploadedFile) {
      return
    }

    processFile(uploadedFile)
    event.target.value = ''
  }

  const handleDrop = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault()
    setIsDragging(false)
    if (event.dataTransfer.files && event.dataTransfer.files[0]) {
      processFile(event.dataTransfer.files[0])
    }
  }

  const handleDragOver = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = () => {
    setIsDragging(false)
  }

  const handleReset = () => {
    setFileName('')
    setLocalSales([])
    setError('')
    onSalesParsed?.([])
    fileInputRef.current && (fileInputRef.current.value = '')
  }

  const monthNameToNumber = (month: string): number => {
    if (!month) {
      return 0
    }

    const index = MONTH_OPTIONS.findIndex((m) => m.value === month)
    // MONTH_OPTIONS starts with "All months" at index 0, so real months start from 1
    return index > 0 ? index : 0
  }

  const handleProceed = () => {
    if (!localSales.length) {
      showFailure('Please upload and parse a sheet before proceeding.')
      return
    }

    if (!selectedMonth || !selectedYear || !selectedChannel) {
      showFailure('Please select Month, Year and Channel before proceeding.')
      return
    }

    setIsSubmitting(true)
    showPageLoader?.()

    const handleSuccess = (res: AjaxResponse<BulkUploadResponse>) => {
      // Check if the response contains an error
      if (res.response?.error) {
        showFailure(res.response.error)
        hidePageLoader?.()
        setIsSubmitting(false)
        return
      }

      // Check if response has data (success case)
      if (res.response?.data) {
        const message =
          res.response?.message || 'Sales sheet submitted successfully.'
        showSuccess(message)
        hidePageLoader?.()
        setIsSubmitting(false)
        // Reset state after successful submission
        setLocalSales([])
        setFileName('')
        onSalesParsed?.([])
        // Close the slider after successful submission
        if (onClose) {
          setTimeout(() => {
            onClose()
          }, 500) // Small delay to show success message
        }
        return
      }

      // Fallback success message if no data but no error
      const message =
        res.response?.message || 'Sales sheet submitted successfully.'
      showSuccess(message)
      hidePageLoader?.()
      setIsSubmitting(false)
      // Reset state after successful submission
      setLocalSales([])
      setFileName('')
      onSalesParsed?.([])
    }

    const handleError = (err: AjaxError) => {
      showFailure('Unable to submit sales sheet.')
      hidePageLoader?.()
      setIsSubmitting(false)
    }

    const products: SalesHistoryProductInput[] = localSales.map((sale) => ({
      product_sku: sale.productSku,
      product_name: sale.productName,
      qty_sold: sale.quantity ?? 0,
    }))

    const payload: SubmitBulkSalesPayload = {
      sale_channel: selectedChannel,
      sale_year: Number(selectedYear),
      sale_month: monthNameToNumber(selectedMonth),
      // Backend requires a numeric growth_factor; using 1 as a neutral default
      growth_factor: 1,
      products,
    }

    submitBulkSalesUpload(payload).subscribe({
      next: handleSuccess,
      error: handleError,
    })
  }

  return (
    <DcFlexBox
      flexDirection="column"
      padding="24px"
      gap="20px"
      height="100%"
      style={{ overflowY: 'auto' }}
    >
      <DcDiv>
        <DcFlexBox
          height="auto"
          gap="12px"
          style={{ marginBottom: '12px', width: '100%' }}
        >
          <DcDiv style={{ flex: 1 }}>
            <DcDropDown
              label="Month"
              dropdownSize="sm"
              required
              placeholder="All months"
              options={MONTH_OPTIONS}
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
            />
          </DcDiv>
          <DcDiv style={{ flex: 1 }}>
            <DcDropDown
              label="Year"
              required
              dropdownSize="sm"
              placeholder="All years"
              options={YEAR_OPTIONS}
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
            />
          </DcDiv>
          <DcDiv style={{ flex: 1 }}>
            <DcDropDown
              label="Channel"
              dropdownSize="sm"
              required
              placeholder="All channels"
              options={CHANNEL_OPTIONS}
              value={selectedChannel}
              onChange={(e) => setSelectedChannel(e.target.value)}
            />
          </DcDiv>
        </DcFlexBox>

        <DcText style={{ fontSize: '14px', color: '#5c6272' }}>
          Download the &nbsp;
          <a
            style={{ color: colors.primary, cursor: 'pointer' }}
            onClick={handleDownloadTemplate}
            target="_blank"
          >
            template
          </a>
          , add your sales rows with the required columns, and upload the sheet
          back. Required columns: <b>product_sku</b>, <b>product_name</b>,{' '}
          <b>qty_sold</b>.
        </DcText>
      </DcDiv>

      <DcFlexBox height="auto" gap="10px">
        <DcButton
          variant="link"
          size="sm"
          disabled={!fileName && localSales.length === 0}
          onClick={handleReset}
        >
          <PixelIcon color="#5c6272" icon={faRotateLeft} />
          &nbsp;Reset
        </DcButton>
      </DcFlexBox>

      <DropZone
        $isDragging={isDragging}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        htmlFor="bulk-sales-upload-input"
      >
        <PixelIcon color="#5c4dff" icon={faFileArrowUp} />
        <DcText style={{ fontWeight: 600 }}>Drag & drop your sheet</DcText>
        <DcText style={{ fontSize: '13px', color: '#7a7f92' }}>
          or click to select a file
        </DcText>
        <DcText style={{ fontSize: '12px', color: '#9aa0b5' }}>
          Accepted formats: .csv, .xls, .xlsx
        </DcText>
        {fileName && (
          <DcText style={{ fontSize: '13px', color: '#4a5568' }}>
            Selected file: <b>{fileName}</b>
          </DcText>
        )}
        {error && <ErrorText>{error}</ErrorText>}
        <input
          id="bulk-sales-upload-input"
          type="file"
          accept=".csv,.xls,.xlsx"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </DropZone>

      {localSales && localSales.length > 0 ? (
        <>
          <DcDiv>
            <DcText style={{ fontSize: '15px', fontWeight: 600 }}>
              Parsed rows ({localSales.length})
            </DcText>
            <DcText style={{ fontSize: '12px', color: '#7a7f92' }}>
              Preview shows the first 10 records to keep the slider light.
            </DcText>
          </DcDiv>
          <PreviewList>
            {localSales.slice(0, 10).map((sale, index) => (
              <PreviewRow key={`${sale.productSku}-${index}`}>
                <DcText style={{ fontWeight: 600 }}>{sale.productName}</DcText>
                <DcText style={{ fontSize: '13px', color: '#7a7f92' }}>
                  SKU: {sale.productSku} · Qty Sold: {sale.quantity}
                </DcText>
              </PreviewRow>
            ))}
          </PreviewList>
          <DcFlexBox height="auto" justifyContent="flex-end">
            <DcButton
              variant="primary"
              size="sm"
              onClick={handleProceed}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting…' : 'Proceed'}
            </DcButton>
          </DcFlexBox>
        </>
      ) : null}
    </DcFlexBox>
  )
}

export default BulkSalesUpload
